import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiError, OPP_STATUS_LABELS } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, ClipboardCheck, ShieldCheck, XCircle, Edit3, MapPin, Clock,
  Users, Eye, AlertCircle, Filter,
} from "lucide-react";

export default function AdminOpportunitiesQueue() {
  const [allOpps, setAllOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpp, setModalOpp] = useState(null);
  const [decision, setDecision] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("under_review");

  const load = async () => {
    setLoading(true);
    try {
      // Use /opportunities/manage so admin gets submitter_name + admin_notes
      const { data } = await api.get(`/opportunities/manage`);
      setAllOpps(data.opportunities || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const opps = useMemo(() => allOpps.filter((o) => o.status === tab), [allOpps, tab]);

  const openModal = (o, d) => { setModalOpp(o); setDecision(d); setNote(""); };
  const submit = async () => {
    if (!modalOpp) return;
    if (decision !== "approve" && !note.trim()) { toast.error("اكتب ملاحظة للجمعية"); return; }
    setBusy(true);
    try {
      await api.patch(`/opportunities/${modalOpp.id}/moderate`, { decision, note });
      toast.success(decision === "approve" ? "تم قبول الفرصة ونشرها" : decision === "reject" ? "تم رفض الفرصة" : "تم إعادة الفرصة للتعديل");
      setModalOpp(null);
      load();
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "ACTIVE_ASSOCIATION_AGREEMENTS_REQUIRED") {
        // Offer a shortcut to the Contracts page so admin can issue the agreement fast
        toast.error(formatApiError(e), {
          duration: 10000,
          action: {
            label: "افتح العقود",
            onClick: () => { window.location.href = "/dashboard/admin/contracts"; },
          },
        });
      } else {
        toast.error(formatApiError(e));
      }
    }
    finally { setBusy(false); }
  };

  const tabs = [
    { value: "under_review", label: "تحت المراجعة", cls: "bg-amber-50 text-amber-800 border-amber-200" },
    { value: "needs_edit",   label: "بحاجة لتعديل", cls: "bg-orange-50 text-orange-800 border-orange-200" },
    { value: "published",    label: "منشورة",       cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    { value: "rejected",     label: "مرفوضة",       cls: "bg-rose-50 text-rose-700 border-rose-200" },
  ];

  return (
    <DashboardLayout>
      <div data-testid="admin-opportunities-queue">
        <div className="mb-6">
          <div className="text-xs font-bold uppercase tracking-widest text-teal-700">لوحة النظام</div>
          <h1 className="mt-2 text-2xl font-heading font-black text-slate-900">إدارة الفرص التدريبية</h1>
          <p className="mt-2 text-slate-600 max-w-3xl">
            الفرص المُقدَّمة من الجمعيات بانتظار قرارك. اضغط «قبول» لنشرها فوراً، أو أعِدها لتعديل مع ملاحظة واضحة، أو ارفضها مع سبب.
          </p>
        </div>

        <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="queue-tabs">
          {tabs.map((t) => (
            <button key={t.value} onClick={() => setTab(t.value)} className={`px-3.5 py-1.5 rounded-lg text-sm font-bold border ${tab === t.value ? t.cls + " ring-2 ring-teal-500" : "bg-white border-cream-200 text-slate-600 hover:text-slate-900"}`} data-testid={`tab-${t.value}`}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : opps.length === 0 ? (
          <div className="p-10 rounded-2xl border border-dashed border-slate-300 bg-white text-center text-slate-500">
            {tab === "under_review" ? "لا توجد فرص بانتظار المراجعة الآن 🎉" : "لا نتائج في هذا القسم."}
          </div>
        ) : (
          <div className="space-y-3" data-testid="queue-list">
            {opps.map((o) => (
              <div key={o.id} className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid={`queue-${o.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-heading font-bold text-slate-900">{o.title}</h3>
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-bold border bg-amber-50 text-amber-800 border-amber-200">{OPP_STATUS_LABELS[o.status]}</span>
                    </div>
                    {(o.submitter_name || o.association_name) && (
                      <div className="mb-1 inline-flex items-center gap-1.5 text-[11px] font-bold text-teal-800 bg-teal-50 border border-teal-100 rounded-full px-2 py-0.5" data-testid={`queue-submitter-${o.id}`}>
                        <Users className="h-3 w-3" />
                        <span>الجمعية المُقدِّمة: {o.submitter_name || o.association_name}</span>
                      </div>
                    )}
                    {o.program_name && <div className="text-xs text-teal-700 font-medium mb-1">{o.program_name}</div>}
                    <p className="text-sm text-slate-700 line-clamp-2">{o.description}</p>
                    <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-500">
                      {o.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{o.city}</span>}
                      <span className="inline-flex items-center gap-1 arabic-numerals"><Clock className="h-3.5 w-3.5" />{o.duration_weeks} أسبوع</span>
                      <span className="inline-flex items-center gap-1 arabic-numerals"><Users className="h-3.5 w-3.5" />{o.seats} مقعد</span>
                    </div>
                    {o.admin_notes && (
                      <div className="mt-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs" data-testid={`queue-admin-notes-${o.id}`}>
                        <div className="font-bold text-amber-900 mb-0.5">ملاحظة الجمعية للإدارة</div>
                        <div className="text-amber-900 whitespace-pre-line">{o.admin_notes}</div>
                      </div>
                    )}
                    {o.moderation_note && (
                      <div className="mt-2 p-2.5 rounded-lg bg-cream-50 border border-cream-200 text-xs">
                        <div className="font-bold text-slate-700">ملاحظة سابقة</div>
                        <div className="text-slate-700 whitespace-pre-line">{o.moderation_note}</div>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <Link to={`/opportunities/${o.id}`} target="_blank" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-teal-700 hover:bg-teal-50 text-xs font-bold" data-testid={`view-${o.id}`}>
                      <Eye className="h-3.5 w-3.5" /> فتح
                    </Link>
                    {tab !== "published" && (
                      <button onClick={() => openModal(o, "approve")} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700" data-testid={`approve-${o.id}`}>
                        <ShieldCheck className="h-3.5 w-3.5" /> قبول
                      </button>
                    )}
                    {tab !== "rejected" && (
                      <>
                        <button onClick={() => openModal(o, "needs_edit")} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-bold hover:bg-amber-600" data-testid={`edit-${o.id}`}>
                          <Edit3 className="h-3.5 w-3.5" /> تعديل مطلوب
                        </button>
                        <button onClick={() => openModal(o, "reject")} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-rose-600 text-white text-xs font-bold hover:bg-rose-700" data-testid={`reject-${o.id}`}>
                          <XCircle className="h-3.5 w-3.5" /> رفض
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {modalOpp && (
          <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={(e) => { if (e.target === e.currentTarget) setModalOpp(null); }}>
            <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200" data-testid="moderate-modal">
              <div className="px-4 py-3 border-b border-slate-200">
                <h3 className="font-heading font-bold text-slate-900 text-lg">
                  {decision === "approve" ? "قبول الفرصة" : decision === "reject" ? "رفض الفرصة" : "طلب تعديل"}
                </h3>
                <div className="text-xs text-slate-500 mt-0.5">{modalOpp.title}</div>
              </div>
              <div className="p-4">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                  {decision === "approve" ? "ملاحظة تشجيعية (اختياري)" : "ملاحظتك للجمعية (مطلوبة)"}
                </label>
                <textarea rows={8} value={note} onChange={(e) => setNote(e.target.value)} className="w-full min-h-48 rounded-lg border border-slate-300 px-3 py-3 text-sm whitespace-pre-wrap" placeholder={decision === "approve" ? "شكر أو توجيه" : decision === "reject" ? "اذكر سبب الرفض" : "اذكر التعديلات المطلوبة"} data-testid="mod-note" />
              </div>
              <div className="px-4 py-3 border-t border-slate-100 flex gap-2 justify-end">
                <button onClick={() => setModalOpp(null)} className="px-4 py-2 rounded-lg text-slate-700 hover:bg-slate-100">إلغاء</button>
                <button onClick={submit} disabled={busy} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-semibold disabled:opacity-60 ${decision === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : decision === "reject" ? "bg-rose-600 hover:bg-rose-700" : "bg-amber-500 hover:bg-amber-600"}`} data-testid="mod-confirm">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />} تأكيد
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
